import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import DrawCanvas from "./DrawCanvas";
import DeleteIcon from "@mui/icons-material/Delete";
import PenToolIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import TextIcon from "@mui/icons-material/Title";
import ImagePicker from "./ImagePicker";
import TypeText from "./TypeText";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { post } from "../apis/apis";
import toastAlert from "./toastAlert";

const SignatureModalContent = ({
  initialsBox,
  profile,
  returnedSignature,
  modalClose,
  user_id_user,
  user_email,
  modalClosingD,
  receiverSide,
  primaryColorComp,
  existingSignatureUrl,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLandscapeMedia = useMediaQuery('(orientation: landscape)');

  // Active orientation: update on resize/orientationchange so layout switches when device rotates
  const [dimensions, setDimensions] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));
  const isLandscapeBySize = dimensions.width > dimensions.height && dimensions.width >= 480;
  const isLandscape = isLandscapeMedia || (isMobile && isLandscapeBySize);
  // In landscape, phone width often > 600px so isMobile is false. Use right-side bar when landscape + (tablet or short height)
  const isRightSideControls = isLandscape && (isTablet || dimensions.height < 600);

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('orientationchange', () => {
      setTimeout(updateDimensions, 0);
      setTimeout(updateDimensions, 100);
      setTimeout(updateDimensions, 300);
    });
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const [activeTab, setActiveTab] = useState(1);

  const [savedSignatures, setSavedSignatures] = useState([]);
  const [deletingSignatureId, setDeletingSignatureId] = useState(null);
  const [selectedProfileSignatureUrl, setSelectedProfileSignatureUrl] = useState(null);
  const [selectedProfileSignatureId, setSelectedProfileSignatureId] = useState(null);
  const [prefillDrawSaveAsProfile, setPrefillDrawSaveAsProfile] = useState(false);
  const [drawColor, setDrawColor] = useState("black");
  const [typeTextColor, setTypeTextColor] = useState("#000000");

  const canvasRef = useRef(null);
  const imagePickerRef = useRef(null);
  const typeTextRef = useRef(null);
  const handleSaveSignature = async () => {
    if (activeTab === 1 && canvasRef && canvasRef.handleSave) {
      await canvasRef.handleSave();
    }
    modalClose();
  };
  const handleSelectSavedSignature = (signature) => {
    const signatureId = signature.user_signature_id || signature.id || signature.signature_id || signature.signature_image_url;
    setSelectedProfileSignatureUrl(signature.signature_image_url);
    setSelectedProfileSignatureId(signatureId);
  };
  const handleCreateProfileSignature = () => {
    setPrefillDrawSaveAsProfile(true);
    setActiveTab(1);
  };
  const handleTextSignature = (imageData) => {
    returnedSignature(imageData, "text");
    // Don't close modal - allow user to add more signatures
    toastAlert("success", initialsBox ? "Initial added successfully." : "Signature added successfully.");
  };

  // Handle delete signature
  const handleDeleteSignature = async (signatureId, e) => {
    e.stopPropagation(); // Prevent selecting the signature when deleting
    if (!signatureId) return;

    setDeletingSignatureId(signatureId);
    try {
      const postData = {
        user_id: user_id_user,
        user_signature_id: signatureId,
      };
      const apiData = await post("user/DeleteSignature", postData);

      if (apiData.error) {
        toastAlert("error", apiData.message || "Failed to delete signature");
        setDeletingSignatureId(null);
        return;
      }

      toastAlert("success", apiData.message || "Signature deleted successfully");

      // Refetch signatures to ensure we have the latest data from server
      // This will show the loader during refetch
      await fetchSignatures();
    } catch (error) {
      console.error("Error deleting signature:", error);
      toastAlert("error", "Failed to delete signature");
      // Refetch on error to ensure consistency - this will show loader
      await fetchSignatures();
    } finally {
      setDeletingSignatureId(null);
    }
  };

  const tabItems1 = [
    !receiverSide && {
      label: "profile",
      icon: <AccountCircleIcon />,
      id: 0,
    },
    { label: "draw", icon: <PenToolIcon />, id: 1 },
    { label: "image", icon: <ImageIcon />, id: 2 },
    { label: "text", icon: <TextIcon />, id: 3 },
  ];
  const tabItems = [
    { label: "draw", icon: <PenToolIcon />, id: 1 },
    { label: "image", icon: <ImageIcon />, id: 2 },
    { label: "text", icon: <TextIcon />, id: 3 },
  ];
  const [loadingData, setLoadingData] = useState(false);

  // Refetch signatures function (stable so children can call after saving to profile)
  const fetchSignatures = useCallback(async () => {
    if (!receiverSide && user_id_user) {
      setLoadingData(true);
      try {
        const postData = {
          user_id: user_id_user,
          type: initialsBox ? "profile_initils" : "profile",
        };
        const apiData = await post("user/GetUserSignaturesToDb", postData);
        const list = apiData && !apiData.error ? (apiData.data || []) : [];
        setSavedSignatures(list);
        if (list.length > 0) {
          const first = list[0];
          const firstId = first.user_signature_id || first.id || first.signature_id || first.signature_image_url;
          setSelectedProfileSignatureUrl((prev) => prev || first.signature_image_url);
          setSelectedProfileSignatureId((prev) => prev || firstId);
        } else {
          setSelectedProfileSignatureUrl(null);
          setSelectedProfileSignatureId(null);
        }
      } catch (error) {
        console.error("Error fetching signatures:", error);
        setSavedSignatures([]);
        setSelectedProfileSignatureUrl(null);
        setSelectedProfileSignatureId(null);
      } finally {
        setLoadingData(false);
      }
    }
  }, [initialsBox, user_id_user, receiverSide]);

  useEffect(() => {
    fetchSignatures();
  }, [fetchSignatures]);

  // Refetch signatures when switching to profile tab to ensure fresh data
  useEffect(() => {
    if (activeTab === 0 && !receiverSide) {
      fetchSignatures();
    }
  }, [activeTab, fetchSignatures, receiverSide]);

  const handleBarDone = async () => {
    if (activeTab === 0 && selectedProfileSignatureUrl) {
      returnedSignature(selectedProfileSignatureUrl, "profile");
      toastAlert("success", initialsBox ? "Initial added successfully" : "Signature added successfully");
    } else if (activeTab === 1 && canvasRef?.current?.handleSave) {
      await canvasRef.current.handleSave();
    } else if (activeTab === 2 && imagePickerRef?.current?.submit) {
      imagePickerRef.current.submit();
    } else if (activeTab === 3 && typeTextRef?.current?.submit) {
      typeTextRef.current.submit();
    }
    // Profile tab (activeTab === 0): do not close on Done — same as Draw/Text/Image, allow adding multiple signatures (doc and sign).
  };

  const handleBarClear = () => {
    if (activeTab === 1 && canvasRef?.current?.handleClear) {
      canvasRef.current.handleClear();
    }
  };

  useEffect(() => {
    if (!savedSignatures.length) {
      setSelectedProfileSignatureUrl(null);
      setSelectedProfileSignatureId(null);
      return;
    }
    const exists = savedSignatures.some((signature) => {
      const signatureId = signature.user_signature_id || signature.id || signature.signature_id || signature.signature_image_url;
      return signatureId === selectedProfileSignatureId;
    });
    if (!exists) {
      const first = savedSignatures[0];
      const firstId = first.user_signature_id || first.id || first.signature_id || first.signature_image_url;
      setSelectedProfileSignatureUrl(first.signature_image_url);
      setSelectedProfileSignatureId(firstId);
    }
  }, [savedSignatures, selectedProfileSignatureId]);

  // Keep all signature input tabs available on mobile and desktop.
  const getFilteredTabs = () => {
    const baseTabs = !receiverSide ? tabItems1.filter(Boolean) : tabItems;
    return baseTabs;
  };
  const tabList = getFilteredTabs();
  const primaryColor = primaryColorComp ?? "rgb(0, 145, 255)";

  return (
    <>
      <DialogTitle
        sx={{
          p: 0,
          py: 0,
          width: "100%",
          maxWidth: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          flexShrink: 0,
          m: 0,
          minHeight: { xs: isLandscape ? "36px" : "48px", sm: "48px" },
          maxHeight: { xs: isLandscape ? "36px" : "48px", sm: "48px" },
          overflowX: "hidden",
          position: "relative",
          px: 2,
        }}
      >
        <Box component="span" sx={{ fontSize: { xs: "14px", sm: "16px" }, fontWeight: 500 }}>
          {initialsBox ? "Initial" : "Add Signature"}
        </Box>
        <IconButton
          onClick={modalClosingD}
          size="small"
          sx={{
            flexShrink: 0,
            backgroundColor: "red",
            color: "white",
            "&:hover": { backgroundColor: "#d32f2f" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Portrait: content top, controls bottom. Landscape: content left, all controls fixed on right in 2 columns */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isRightSideControls ? "row" : "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          width: "100%",
          position: "relative",
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            flex: 1,
            overflow: "hidden",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            m: 0,
            width: isRightSideControls ? "auto" : "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{
            height: "100%",
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            overflowX: "hidden",
            boxSizing: "border-box",
          }}>

            <Box sx={{
              p: 0,
              flex: 1,
              overflow: "hidden",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              position: "relative",
              width: "100%",
              maxWidth: "100%",
              height: "100%",
              boxSizing: "border-box",
              // Removed spacing for all orientations
            }}>
              {activeTab === 0 && !receiverSide && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    textAlign: "center",
                    color: "gray",
                    border: "1px dashed #ccc",
                    borderRadius: "8px",
                    p: 0,
                  }}
                >
                  <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", p: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleCreateProfileSignature}
                      sx={{
                        textTransform: "none",
                        mr: 1.5,
                        bgcolor: "rgb(0, 145, 255)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgb(0, 125, 225)" },
                      }}
                    >
                      {initialsBox ? "Create Initial" : "Create Signature"}
                    </Button>
                  </Box>
                  <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                    {savedSignatures.length > 0 ? (
                      <Grid container spacing={1.25} justifyContent="flex-start" sx={{ p: 1 }}>
                        {savedSignatures.map((signature) => {
                          // Use unique identifier as key - try multiple possible field names
                          const signatureId = signature.user_signature_id || signature.id || signature.signature_id || signature.signature_image_url;
                          const isDeleting = deletingSignatureId === signatureId;
                          const isSelected = selectedProfileSignatureId === signatureId;

                          return (
                            <Grid item xs={12} sm={6} md={4} key={signatureId}>
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 100,
                                  border: isSelected ? "2px solid rgb(0, 145, 255)" : "1px solid #ccc",
                                  borderRadius: "8px",
                                  overflow: "hidden",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  position: "relative",
                                  opacity: isDeleting ? 0.6 : 1,
                                  backgroundColor: "#fff",
                                  boxShadow: isSelected ? "0 0 0 1px rgba(0, 145, 255, 0.2)" : "none",
                                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                                }}
                                onClick={() => !isDeleting && handleSelectSavedSignature(signature)}
                              >
                                <img
                                  src={signature.signature_image_url}
                                  alt={initialsBox ? `Initial ${signatureId}` : `Signature ${signatureId}`}
                                  width="100%"
                                  height="100%"
                                  style={{
                                    objectFit: "contain",
                                    objectPosition: "center",
                                  }}
                                />
                                {/* Delete button */}
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleDeleteSignature(signatureId, e)}
                                  disabled={isDeleting}
                                  sx={{
                                    position: "absolute",
                                    top: 4,
                                    right: 4,
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                    color: "#d32f2f",
                                    padding: "4px",
                                    zIndex: 10,
                                    "&:hover": {
                                      backgroundColor: "rgba(255, 255, 255, 1)",
                                      color: "#b71c1c",
                                    },
                                    "&:disabled": {
                                      opacity: 0.5,
                                    },
                                  }}
                                >
                                  {isDeleting ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                                {isSelected && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      bottom: 6,
                                      right: 6,
                                      width: 22,
                                      height: 22,
                                      borderRadius: "50%",
                                      backgroundColor: "rgb(0, 145, 255)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#fff",
                                      border: "1px solid #ffffff",
                                      zIndex: 11,
                                    }}
                                  >
                                    <CheckIcon sx={{ fontSize: 16 }} />
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    ) : !loadingData ? (
                      <Typography
                        variant="body1"
                        sx={{ fontSize: "16px", fontWeight: "500" }}
                      >
                        {initialsBox ? "No initial saved yet" : "No signature saved yet"}
                      </Typography>
                    ) : null}

                    {/* Loader overlay when refetching after deletion */}
                    {loadingData && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          zIndex: 1000,
                          borderRadius: "8px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <CircularProgress
                            size={40}
                            sx={{
                              color:
                                primaryColorComp === null ||
                                  primaryColorComp === undefined
                                  ? "rgb(0, 145, 255)"
                                  : primaryColorComp,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(0, 0, 0, 0.7)",
                              fontSize: { xs: "12px", sm: "14px" },
                            }}
                          >
                            {initialsBox ? "Updating initials..." : "Updating signatures..."}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ position: "relative", width: "100%", height: "100%", flex: 1, minHeight: 0 }}>
                  <DrawCanvas
                    primaryColorComp={primaryColorComp}
                    receiverSide={receiverSide}
                    ref={canvasRef}
                    user_id_user={user_id_user}
                    penColor={drawColor}
                    saveSignature={(dataURL) => {
                      returnedSignature(dataURL);
                      toastAlert("success", initialsBox ? "Initial added successfully." : "Signature added successfully.");
                    }}
                    modalClose={() => { }}
                    initialsBox={initialsBox}
                    modalClosingD={modalClosingD}
                    existingSignatureUrl={existingSignatureUrl}
                    onProfileSaved={fetchSignatures}
                    defaultSaveAsProfile={prefillDrawSaveAsProfile}
                    onDefaultSaveApplied={() => setPrefillDrawSaveAsProfile(false)}
                  />
                  <Button
                    variant="contained"
                    onClick={handleBarClear}
                    size="small"
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      right: 40,
                      textTransform: "none",
                      fontSize: { xs: "12px", sm: "13px" },
                      fontWeight: 600,
                      bgcolor: "red",
                      color: "white",
                      minWidth: "auto",
                      px: 1.5,
                      py: 0.75,
                      boxShadow: 1,
                      "&:hover": { bgcolor: "#d32f2f" },
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              )}
              {activeTab === 2 && (
                <Box sx={{ p: 0, textAlign: "center", width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
                  <ImagePicker
                    ref={imagePickerRef}
                    hideFooter
                    primaryColorComp={primaryColorComp}
                    receiverSide={receiverSide}
                    user_id_user={user_id_user}
                    user_email={user_email}
                    onSelectImage={(imageUrl) => {
                      returnedSignature(imageUrl);
                      toastAlert("success", initialsBox ? "Initial added successfully." : "Signature added successfully.");
                    }}
                    modalClose={() => { }}
                    initialsBox={initialsBox}
                    modalClosingD={modalClosingD}
                    onProfileSaved={fetchSignatures}
                  />
                </Box>
              )}
              {activeTab === 3 && (
                <Box sx={{ p: 0, width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
                  <TypeText
                    ref={typeTextRef}
                    hideFooter
                    selectedColor={typeTextColor}
                    onColorChange={setTypeTextColor}
                    primaryColorComp={primaryColorComp}
                    receiverSide={receiverSide}
                    user_id_user={user_id_user}
                    user_email={user_email}
                    user_first_name=""
                    user_last_name=""
                    initialsBox={initialsBox}
                    profile={profile}
                    modalClosingD={modalClosingD}
                    modalClose={modalClose}
                    onSaveText={(imageData) => handleTextSignature(imageData)}
                    onProfileSaved={fetchSignatures}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        {/* Mobile portrait: bar at bottom. Mobile landscape: 2-column grid on right side */}
        <Box
          sx={{
            flexShrink: 0,
            display: isRightSideControls ? "grid" : "flex",
            gridTemplateColumns: isRightSideControls ? "1fr 1fr" : undefined,
            gridTemplateRows: isRightSideControls ? "1fr" : undefined,
            flexDirection: isRightSideControls ? undefined : { xs: "column", sm: "row" },
            alignItems: isRightSideControls ? "stretch" : { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            width: isRightSideControls ? "auto" : "100%",
            minWidth: isRightSideControls ? "140px" : undefined,
            maxWidth: isRightSideControls ? "160px" : undefined,
            boxSizing: "border-box",
            px: isRightSideControls ? 0.5 : { xs: 1, sm: 3 },
            pr: isRightSideControls ? "calc(6px + env(safe-area-inset-right, 0px))" : { xs: 1.5, sm: 4 },
            py: isRightSideControls ? 0.75 : { xs: 0.5, sm: 1.5 },
            pb: isRightSideControls
              ? "calc(6px + env(safe-area-inset-bottom, 0px))"
              : { xs: "calc(6px + env(safe-area-inset-bottom, 0px))", sm: "12px" },
            borderTop: isRightSideControls ? "none" : "1px solid rgba(0, 0, 0, 0.12)",
            borderLeft: isRightSideControls ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
            backgroundColor: "white",
            gap: isRightSideControls ? 0.5 : { xs: 0.25, sm: 1 },
            position: "relative",
            zIndex: 10,
            pointerEvents: "auto",
            overflow: "visible",
          }}
        >
          {/* Column 1: Tabs + Clear + Ink colors (vertical stack) */}
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
            gap: 0.5,
            borderRight: isRightSideControls ? "1px solid rgba(0,0,0,0.08)" : "none",
            pr: isRightSideControls ? 0.5 : 0,
          }}>
            {/* Tabs - vertical in landscape */}
            <Box sx={{
              display: "flex",
              flexDirection: isRightSideControls ? "column" : "row",
              gap: 0,
              alignItems: isRightSideControls ? "stretch" : "center",
              justifyContent: isRightSideControls ? "flex-start" : { xs: "center", sm: "flex-start" },
            }}>
              {tabList.map((tab) => (
                <Button
                  key={tab.id}
                  startIcon={tab.icon}
                  onClick={() => setActiveTab(tab.id)}
                  sx={{
                    textTransform: "none",
                    fontSize: isRightSideControls ? "10px" : { xs: "11px", sm: "14px" },
                    color: activeTab === tab.id ? primaryColor : "rgba(0,0,0,0.6)",
                    borderLeft: isRightSideControls ? (activeTab === tab.id ? `3px solid ${primaryColor}` : "3px solid transparent") : "none",
                    borderBottom: isRightSideControls ? "none" : (activeTab === tab.id ? `2px solid ${primaryColor}` : "2px solid transparent"),
                    borderRadius: 0,
                    minWidth: "auto",
                    px: isRightSideControls ? 0.5 : { xs: 1, sm: 1.5 },
                    py: isRightSideControls ? 0.4 : { xs: 0.5, sm: 1 },
                    justifyContent: isRightSideControls ? "flex-start" : "center",
                    "& .MuiButton-startIcon": {
                      mr: isRightSideControls ? 0.5 : { xs: 0.3, sm: 1 },
                      "& .MuiSvgIcon-root": {
                        fontSize: isRightSideControls ? "14px" : undefined,
                      },
                    },
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.04)",
                      color: primaryColor,
                    },
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>

            {/* Ink colors (Clear is on canvas bottom-right when draw tab) */}
            {(activeTab === 1 || activeTab === 3) && (
              <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.5,
                pt: 0.5,
                borderTop: isRightSideControls ? "1px solid rgba(0,0,0,0.08)" : "none",
              }}>
                {/* Ink colors - 2x2 grid in landscape */}
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: isRightSideControls ? "1fr 1fr" : "repeat(4, 1fr)",
                  gap: isRightSideControls ? "4px" : { xs: "4px", sm: "6px" },
                  alignItems: "center",
                  width: "100%",
                }}>
                  {(activeTab === 1 ? ["black", "blue", "#23b3e8", "#4A6BA8"] : ["#000000", "#0000FF", "#23b3e8", "#4A6BA8"]).map((colorOption) => (
                    <Box
                      key={colorOption}
                      onClick={() => {
                        if (activeTab === 1) {
                          setDrawColor(colorOption);
                          canvasRef.current?.recolorTo?.(colorOption);
                        } else {
                          setTypeTextColor(colorOption);
                        }
                      }}
                      sx={{
                        width: isRightSideControls ? 22 : { xs: 28, sm: 32 },
                        height: isRightSideControls ? 22 : { xs: 28, sm: 32 },
                        backgroundColor: colorOption,
                        borderRadius: 1,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: (activeTab === 1 ? drawColor : typeTextColor) === colorOption ? `2px solid ${primaryColor}` : "1px solid rgba(0,0,0,0.12)",
                        "&:hover": { transform: "scale(1.05)" },
                      }}
                    >
                      {(activeTab === 1 ? drawColor : typeTextColor) === colorOption && <CheckIcon sx={{ color: "#fff", fontSize: isRightSideControls ? 12 : { xs: 16, sm: 18 } }} />}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Column 2: Cancel + Done buttons (vertical stack) */}
          <Box sx={{
            display: "flex",
            flexDirection: isRightSideControls ? "column" : "row",
            alignItems: "stretch",
            gap: isRightSideControls ? 0.75 : { xs: 1, sm: 1 },
            justifyContent: isRightSideControls ? "flex-start" : { xs: "stretch", sm: "flex-end" },
            pl: isRightSideControls ? 0.5 : 0,
            width: isRightSideControls ? "auto" : { xs: "100%", sm: "auto" },
          }}>
            <Button
              variant="outlined"
              onClick={modalClosingD}
              sx={{
                textTransform: "none",
                fontSize: isRightSideControls ? "11px" : { xs: "15px", sm: "14px" },
                fontWeight: isRightSideControls ? 500 : { xs: 600, sm: 500 },
                px: isRightSideControls ? 1 : { xs: 2, sm: 2 },
                py: isRightSideControls ? 0.5 : { xs: 1.2, sm: 0.5 },
                flex: isRightSideControls ? "none" : { xs: 1, sm: "none" },
                borderColor: "rgba(0,0,0,0.3)",
                color: "rgba(0,0,0,0.7)",
                borderRadius: isRightSideControls ? "6px" : { xs: "8px", sm: "4px" },
                minWidth: "auto",
                minHeight: isRightSideControls ? 36 : undefined,
                "&:hover": { borderColor: "rgba(0,0,0,0.5)", backgroundColor: "rgba(0,0,0,0.04)" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleBarDone}
              disabled={activeTab === 0 && !selectedProfileSignatureUrl}
              sx={{
                bgcolor: primaryColor,
                textTransform: "none",
                fontSize: isRightSideControls ? "11px" : { xs: "15px", sm: "14px" },
                fontWeight: isRightSideControls ? 500 : { xs: 600, sm: 500 },
                px: isRightSideControls ? 1 : { xs: 2, sm: 2 },
                py: isRightSideControls ? 0.5 : { xs: 1.2, sm: 0.5 },
                flex: isRightSideControls ? "none" : { xs: 1, sm: "none" },
                borderRadius: isRightSideControls ? "6px" : { xs: "8px", sm: "4px" },
                minWidth: "auto",
                minHeight: isRightSideControls ? 36 : undefined,
                "&:hover": { bgcolor: primaryColor, opacity: 0.9 },
              }}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SignatureModalContent;
